import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Copy, ExternalLink, CreditCard } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'

const PaymentInfo = () => {
  const [unlockCode, setUnlockCode] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const { toast } = useToast()

  useEffect(() => {
    const fetchUnlockCode = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('assigned_unlock_code, payment_status')
            .eq('id', user.id)
            .single()
          
          if (profile?.assigned_unlock_code) {
            setUnlockCode(profile.assigned_unlock_code)
          }
        }
      } catch (error) {
        console.error('Error fetching unlock code:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchUnlockCode()
  }, [])

  const handleCopyCode = () => {
    navigator.clipboard.writeText(unlockCode)
    toast({
      title: "Code copied!",
      description: "Your unlock code has been copied to clipboard.",
    })
  }

  const handlePaymentClick = () => {
    const stripePaymentLink = import.meta.env.VITE_STRIPE_PAYMENT_LINK
    if (stripePaymentLink) {
      window.open(stripePaymentLink, '_blank')
    } else {
      toast({
        title: "Payment link not configured",
        description: "Please contact support to complete your payment.",
        variant: "destructive",
      })
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-purple-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-purple-100 p-4">
      <div className="w-full max-w-2xl">
        <Card className="shadow-xl">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-purple-100">
              <CreditCard className="h-6 w-6 text-purple-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">
              Complete Your Payment
            </CardTitle>
            <CardDescription className="text-lg">
              Unlock full access to the Dably platform
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Unlock Code Display */}
            {unlockCode && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-700">Your Unlock Code</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyCode}
                    className="flex items-center gap-2"
                  >
                    <Copy className="h-4 w-4" />
                    Copy
                  </Button>
                </div>
                <div className="bg-white p-3 rounded border-2 border-dashed border-gray-300">
                  <code className="text-lg font-mono tracking-wider text-center block text-purple-600">
                    {unlockCode}
                  </code>
                </div>
                <p className="text-sm text-gray-600 mt-2 text-center">
                  Save this code! You'll need it after payment to unlock your account.
                </p>
              </div>
            )}

            {/* Payment Instructions */}
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center text-sm font-semibold text-purple-600">
                  1
                </div>
                <div>
                  <h4 className="font-semibold">Go to Stripe Checkout</h4>
                  <p className="text-gray-600">
                    Click the button below to open the secure Stripe checkout page and complete your payment.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center text-sm font-semibold text-purple-600">
                  2
                </div>
                <div>
                  <h4 className="font-semibold">Enter Your Unlock Code</h4>
                  <p className="text-gray-600">
                    On the Stripe checkout page, enter the unlock code shown on this page. After payment, our admin
                    team will verify your payment and grant you access to the platform.
                  </p>
                </div>
              </div>
            </div>

            {/* Payment Button */}
            <div className="space-y-4">
              <Button
                onClick={handlePaymentClick}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 text-lg"
                size="lg"
              >
                <ExternalLink className="mr-2 h-5 w-5" />
                Pay Now - $29.99
              </Button>
            </div>

            {/* Additional Info */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold text-blue-900 mb-2">What happens next?</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Complete payment through Stripe's secure checkout using your unlock code</li>
                <li>• Our admin team verifies your payment</li>
                <li>• After verification, enter your unlock code to unlock your account</li>
                <li>• Your account will be permanently unlocked with full access</li>
              </ul>
            </div>

            {/* Support */}
            <div className="text-center text-sm text-gray-500">
              Need help? Contact support at{' '}
              <a href="mailto:lucas@dably.co" className="text-purple-600 hover:underline">
                lucas@dably.co
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default PaymentInfo
